// App
import React from 'react';

// Misc / utils
import IconCarat from 'images/icons/arrow-grey.svg';
import { cx } from '@/styled-system/css';
import * as styles from './button.styles';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaExpanded?: boolean;
  ariaLabeledBy?: string;
  buttonType?: string;
  ariaDisabled?: boolean;
  active?: boolean;
  icon?: string;
  iconWidth?: string;
  iconHeight?: string;
  iconAriaAltText?: string;
  showCarat?: boolean;
  flex?: boolean;
  justifyContent?: string;
  alignItems?: string;
  large?: boolean;
  stretch?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaLabel,
      ariaDescribedBy,
      ariaExpanded,
      ariaLabeledBy,
      buttonType,
      className,
      disabled = false,
      ariaDisabled = false,
      active = false,
      icon,
      iconWidth = '12px',
      iconHeight = '12px',
      iconAriaAltText = '',
      onClick,
      onKeyDown,
      children,
      showCarat,
      flex,
      justifyContent,
      alignItems,
      large,
      stretch,
      ...rest
    },
    ref,
  ) => {
    // Build button class name based on props
    const buttonClassName = cx(
      styles.button,
      buttonType === 'text' && styles.buttonTypeText,
      buttonType === 'link' && styles.buttonTypeLink,
      buttonType === 'primary' && styles.buttonTypePrimary,
      buttonType === 'primary-outline' && styles.buttonTypePrimaryOutline,
      large && styles.buttonLarge,
      stretch && styles.buttonStretch,
      disabled && styles.buttonDisabled,
      ariaDisabled && styles.buttonAriaDisabled,
      active && styles.buttonActive,
      className,
    );

    // Build flex container class name if needed
    const flexClassName =
      justifyContent || alignItems ? styles.flexContainerWithAlign(justifyContent, alignItems) : styles.flexContainer;

    return (
      <button
        onClick={onClick}
        ref={ref}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabeledBy}
        aria-expanded={ariaExpanded}
        className={buttonClassName}
        disabled={disabled}
        onKeyDown={onKeyDown}
        aria-disabled={ariaDisabled}
        {...rest}
      >
        {flex ? (
          <div className={flexClassName}>
            {icon && (
              <img
                src={icon}
                width={iconWidth}
                height={iconHeight}
                loading="lazy"
                alt={iconAriaAltText}
                className={styles.icon}
              />
            )}
            {children}
            {showCarat && (
              <span aria-hidden="true" className={styles.caratSpan}>
                <img
                  src={IconCarat}
                  width="12px"
                  height="12px"
                  alt=""
                  className={cx(styles.carat, ariaExpanded && styles.caratActive)}
                  fetchpriority="low"
                  decoding="async"
                />
              </span>
            )}
          </div>
        ) : (
          <>
            {icon && (
              <img
                src={icon}
                width={iconWidth}
                height={iconHeight}
                loading="lazy"
                alt={iconAriaAltText}
                className={styles.icon}
              />
            )}
            {children}
            {showCarat && (
              <span aria-hidden="true" className={styles.caratSpan}>
                <img
                  src={IconCarat}
                  width="12px"
                  height="12px"
                  alt=""
                  className={cx(styles.carat, ariaExpanded && styles.caratActive)}
                  fetchpriority="low"
                  decoding="async"
                />
              </span>
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
